import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, useAuth } from "../App";
import { Heart, MapPin, ShieldCheck, Globe, Phone, ArrowRight, X, Users } from "lucide-react";
import PayButton from "../components/PayButton";
import useGeo from "../hooks/useGeo";
import GeoToggle from "../components/GeoToggle";

export default function Ngos() {
  const [ngos, setNgos] = useState([]);
  const geo = useGeo(25);
  useEffect(() => { axios.get(`${API}/ngos`, { params: geo.geoParams }).then(r => setNgos(r.data.ngos)); }, [geo.mode, geo.coords]);

  return (
    <div className="space-y-6" data-testid="ngos-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">NGOs & Shelters</h1>
          <p className="text-ink-soft">Verified non-profits saving animal lives across India. Every rupee helps.</p>
        </div>
        <GeoToggle geo={geo} label="NGOs" />
      </div>
      {geo.isNearby && ngos.length === 0 && (
        <div className="bg-surface-muted border border-line rounded-2xl p-6 text-center text-sm" data-testid="ngos-empty-nearby">
          <div className="font-bold mb-1">No NGOs within {geo.radius} km of you yet.</div>
          <p className="text-ink-soft">You can still help — switch to <button onClick={() => geo.setMode("all")} className="underline font-bold">All India</button> to donate to verified shelters everywhere.</p>
        </div>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {ngos.map(n => (
          <NgoCard key={n.ngo_id} n={n}/>
        ))}
      </div>
      <Link to="/app/partner" className="block text-center text-sm font-bold text-brand hover:underline">
        Run an NGO? Onboard your organisation →
      </Link>
    </div>
  );
}

function NgoCard({ n }) {
  const [donating, setDonating] = useState(false);
  return (
    <article data-testid={`ngo-${n.ngo_id}`} className="bg-surface border border-line rounded-3xl overflow-hidden shadow-soft hover:-translate-y-1 hover:shadow-hover transition-all">
      <div className="relative h-44">
        <img src={n.cover_photo} alt={n.name} className="w-full h-full object-cover"/>
        {n.verified && <div className="absolute top-3 right-3 bg-leaf text-ink rounded-full px-3 py-1 text-[11px] font-bold inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3"/> Verified</div>}
      </div>
      <div className="p-5">
        <h3 className="font-heading text-xl font-extrabold">{n.name}</h3>
        <div className="text-xs text-ink-soft flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3"/>{n.city}{n.distance_km != null && <span className="ml-1 font-bold text-brand">· {n.distance_km} km</span>}</div>
        <p className="text-sm text-ink-soft mt-2 line-clamp-3">{n.mission}</p>
        <div className="mt-3 flex gap-3 text-xs text-ink-soft">
          {n.website && <a href={n.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-brand"><Globe className="w-3 h-3"/>Website</a>}
          {n.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3"/>{n.phone}</span>}
        </div>
        <div className="mt-4 flex gap-2">
          <button data-testid={`donate-${n.ngo_id}`} onClick={() => setDonating(true)} className="flex-1 bg-brand text-white rounded-full py-2.5 text-sm font-bold hover:bg-brand-hover inline-flex items-center justify-center gap-1">
            <Heart className="w-4 h-4"/> Donate
          </button>
        </div>
      </div>
      {donating && <DonateModal ngo={n} onClose={() => setDonating(false)}/>}
    </article>
  );
}

function DonateModal({ ngo, onClose }) {
  const [amount, setAmount] = useState(500);
  const [step, setStep] = useState("amount"); // amount | done
  const PRESETS = [200, 500, 1000, 2500];
  const onSuccess = () => setStep("done");
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md border border-line shadow-soft" data-testid="donate-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl font-extrabold">Donate to {ngo.name}</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-surface-muted"><X className="w-4 h-4"/></button>
        </div>
        {step === "amount" && (
          <>
            <p className="text-sm text-ink-soft">100% of your donation goes directly to {ngo.name}. PetBond does not take a cut.</p>
            <div className="grid grid-cols-4 gap-2 mt-4">
              {PRESETS.map(p => (
                <button key={p} data-testid={`amt-${p}`} onClick={() => setAmount(p)} className={`rounded-full py-2 text-sm font-bold border ${amount === p ? "bg-brand text-white border-brand" : "bg-surface border-line"}`}>₹{p}</button>
              ))}
            </div>
            <input data-testid="donate-amount" type="number" value={amount} onChange={e => setAmount(Number(e.target.value))} className="ainput mt-3"/>
            <div className="mt-4">
              <PayButton
                amount={amount}
                currency="INR"
                country="IN"
                purpose="donation"
                referenceId={ngo.ngo_id}
                label={`Donate ₹${amount}`}
                dataTestid="donate-confirm"
                onSuccess={onSuccess}
              />
            </div>
          </>
        )}
        {step === "done" && (
          <div className="py-6 text-center">
            <Heart className="w-12 h-12 text-brand mx-auto mb-2 fill-brand"/>
            <div className="font-heading text-xl font-extrabold">Thank you!</div>
            <p className="text-sm text-ink-soft mt-1">Your ₹{amount} donation has been recorded.</p>
            <button onClick={onClose} className="mt-4 bg-ink text-white rounded-full px-5 py-2 text-sm font-bold">Close</button>
          </div>
        )}
        <style>{`.ainput{width:100%;border:1px solid #EAE0D5;border-radius:9999px;padding:0.65rem 1rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.ainput:focus{border-color:#FF6B35;}`}</style>
      </div>
    </div>
  );
}
